// react
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CakeIcon from '@mui/icons-material/Cake';
import CategoryIcon from '@mui/icons-material/Category';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import StraightenIcon from '@mui/icons-material/Straighten';

// material ui
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';

// api
import { useGetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMatrikel';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

const getMatrikelMetadataKey = (
  matrikelMetadata: Record<string, unknown> | undefined,
  key: string,
): unknown => {
  if (!matrikelMetadata) return null;
  if (matrikelMetadata?.OverwriteFlag) {
    return matrikelMetadata?.[`Overwrite${key}`] ?? matrikelMetadata?.[key];
  }
  return matrikelMetadata?.[key];
};

interface BuildingInfoSubheaderProps {
  matrikelKey: string;
}

const BuildingInfoSubheader = ({ matrikelKey }: BuildingInfoSubheaderProps) => {
  const { t } = useTranslation(['portfolio-analysis_power']);
  const account = useSelector(getSelectedAccount);

  const { data: matrikelMetadata, isFetching } =
    useGetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetQuery(
      { accountId: account?.AccountId, matrikelKey },
      { skip: !account?.AccountId || !matrikelKey },
    );

  const meta = matrikelMetadata as Record<string, unknown> | undefined;

  const buildingYear = (
    getMatrikelMetadataKey(meta, 'AverageBuildingYear') as
      | number
      | null
      | undefined
  )?.toFixed(0);

  const buildingArea = (
    getMatrikelMetadataKey(meta, 'SumBuildingArea') as number | null | undefined
  )?.toFixed(0);

  const heatInstallation = getMatrikelMetadataKey(
    meta,
    'ExtractedHeatInstallation',
  ) as string | null | undefined;

  const buildingUse = getMatrikelMetadataKey(meta, 'ExtractedBuildingUse') as
    | string
    | null
    | undefined;

  const hasData =
    buildingYear || buildingArea || heatInstallation || buildingUse;

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  if (!hasData) return null;

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        {buildingYear && (
          <Tooltip
            title={t('columns.AverageBuildingYear.header', {
              ns: 'portfolio-analysis_power',
            })}
          >
            <Chip
              icon={<CakeIcon fontSize="small" />}
              label={buildingYear}
              variant="outlined"
            />
          </Tooltip>
        )}
        {buildingArea && (
          <Tooltip
            title={t('columns.SumBuildingArea.header', {
              ns: 'portfolio-analysis_power',
            })}
          >
            <Chip
              icon={<StraightenIcon fontSize="small" />}
              label={`${buildingArea} ${t('columns.SumBuildingArea.label', { ns: 'portfolio-analysis_power' })}`}
              variant="outlined"
            />
          </Tooltip>
        )}
        {heatInstallation && (
          <Tooltip
            title={t('columns.ExtractedHeatInstallation.header', {
              ns: 'portfolio-analysis_power',
            })}
          >
            <Chip
              icon={<LocalFireDepartmentIcon fontSize="small" />}
              label={heatInstallation}
              variant="outlined"
            />
          </Tooltip>
        )}
        {buildingUse && (
          <Tooltip
            title={t('columns.ExtractedBuildingUse.header', {
              ns: 'portfolio-analysis_power',
            })}
          >
            <Chip
              icon={<CategoryIcon fontSize="small" />}
              label={buildingUse}
              variant="outlined"
            />
          </Tooltip>
        )}
      </Box>
    </>
  );
};

export { BuildingInfoSubheader };

export default BuildingInfoSubheader;
